import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PlayableDiagram } from '@/components/PlayableDiagram';
import { generateChordVoicing } from '@/music/voicing';
import { APP_ROUTES } from '@/navigation';
import '@/learn.css';

const C_MAJOR_SEVEN_DROP_3 = generateChordVoicing('C', 'maj7', 0, 'middle', 'drop-3');

const TOP_VOICES = [
  {
    topVoice: '7th · B',
    notes: 'E · C · G · B',
    bass: '3rd · E',
    inversion: '1st inversion',
  },
  {
    topVoice: 'Root · C',
    notes: 'G · E · B · C',
    bass: '5th · G',
    inversion: '2nd inversion',
  },
  {
    topVoice: '3rd · E',
    notes: 'B · G · C · E',
    bass: '7th · B',
    inversion: '3rd inversion',
  },
  {
    topVoice: '5th · G',
    notes: 'C · B · E · G',
    bass: 'Root · C',
    inversion: 'Root position',
  },
] as const;

export function Drop3IntroPage() {
  return (
    <article className="voicing-primer-page">
      <nav className="learn-breadcrumb" aria-label="Breadcrumb">
        <Link to={APP_ROUTES.learn}>Learn</Link>
        <span aria-hidden="true">/</span>
        <Link to={APP_ROUTES.fourNoteVoicings}>Four-note voicings</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Drop 3</span>
      </nav>

      <header className="drop2-hero">
        <p className="learn-eyebrow">Voicing family</p>
        <h1>Drop 3</h1>
        <p>
          A Drop 3 voicing lowers the third voice from the top of a close-position chord by one
          octave. The result is a bass note below a three-note upper structure.
        </p>
      </header>

      <nav className="drop2-toc" aria-label="On this page">
        <a href="#build-drop-3">Building</a>
        <a href="#compare-drop-3">Drop 2 compared</a>
        <a href="#drop-3-top-voices">Top voices and inversions</a>
        <a href="#drop-3-layouts">String layouts</a>
        <a href="#use-drop-3">Uses</a>
      </nav>

      <section id="build-drop-3" className="drop2-section" aria-labelledby="drop3-build-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">01</span>
          <div>
            <h2 id="drop3-build-heading">Building a Drop 3 voicing</h2>
          </div>
        </div>
        <p className="drop2-lede">
          Close-position Cmaj7, low to high, is C, E, G, B. Counting from the top, B is voice 1, G
          is voice 2 and E is voice 3. Lower E by one octave.
        </p>

        <figure className="drop2-transformation">
          <div className="drop2-stack-wrap">
            <p className="drop2-stack-title">Close position</p>
            <div className="drop2-stack" aria-label="Close position, low to high: C, E, G, B">
              <span className="drop2-register">High</span>
              <div className="drop2-voice">
                <strong>B</strong>
                <span>7 · voice 1</span>
              </div>
              <div className="drop2-voice">
                <strong>G</strong>
                <span>5 · voice 2</span>
              </div>
              <div className="drop2-voice drop2-voice--picked">
                <strong>E</strong>
                <span>3 · voice 3</span>
              </div>
              <div className="drop2-voice drop2-voice--root">
                <strong>C</strong>
                <span>1 · voice 4</span>
              </div>
              <span className="drop2-register">Low</span>
            </div>
          </div>

          <div className="drop2-operation" aria-label="Move E down one octave">
            <span>Drop voice 3</span>
            <ArrowRight size={24} aria-hidden="true" />
            <small>E moves down one octave</small>
          </div>

          <div className="drop2-stack-wrap">
            <p className="drop2-stack-title">Drop 3</p>
            <div className="drop2-stack" aria-label="Drop 3 voicing, low to high: E, C, G, B">
              <span className="drop2-register">High</span>
              <div className="drop2-voice">
                <strong>B</strong>
                <span>7</span>
              </div>
              <div className="drop2-voice">
                <strong>G</strong>
                <span>5</span>
              </div>
              <div className="drop2-voice drop2-voice--root">
                <strong>C</strong>
                <span>1 · root</span>
              </div>
              <div className="drop2-voice drop2-voice--dropped">
                <strong>E</strong>
                <span>3 · dropped</span>
              </div>
              <span className="drop2-register">Low</span>
            </div>
          </div>

          <figcaption>
            Low to high: <strong>C–E–G–B</strong> becomes <strong>E–C–G–B</strong>. Only E changes
            octave. The chord tones and the top note B are unchanged.
          </figcaption>
        </figure>

        <div className="drop2-result">
          <div>
            <h3>Cmaj7, lower layout</h3>
            <p>
              The bass E is on the low E string. The A string is muted. C, G and B are on the D, G
              and B strings.
            </p>
            <p className="drop2-hint">
              Press the diagram to play it. Double-click or double-tap it to see the notes and
              frets.
            </p>
          </div>
          {C_MAJOR_SEVEN_DROP_3 && (
            <PlayableDiagram
              className="drop2-example-diagram"
              chord={{
                fingering: C_MAJOR_SEVEN_DROP_3,
                rootDisplay: 'C',
                chordType: 'maj7',
                symbol: 'Cmaj7',
                inversion: 0,
                stringSet: 'middle',
              }}
              caption="1st inversion · 7th on top · lower Drop 3 layout"
            />
          )}
        </div>
      </section>

      <section
        id="compare-drop-3"
        className="drop2-section"
        aria-labelledby="drop3-compare-heading"
      >
        <div className="drop2-section-heading">
          <span aria-hidden="true">02</span>
          <div>
            <h2 id="drop3-compare-heading">Drop 2 and Drop 3 compared</h2>
          </div>
        </div>
        <p className="drop2-lede">
          Both voicings below have B on top. Drop 2 keeps the four voices close together. Drop 3
          puts the third in the bass and leaves C–G–B above it.
        </p>
        <div className="voicing-family-pair">
          <article>
            <p className="learn-eyebrow">Drop 2</p>
            <h3>G · C · E · B</h3>
            <p>Evenly spread. Fits four adjacent strings.</p>
          </article>
          <article>
            <p className="learn-eyebrow">Drop 3</p>
            <h3>E · C · G · B</h3>
            <p>
              A wider gap above the bass. Usually played with the string above the bass skipped.
            </p>
          </article>
        </div>
      </section>

      <section
        id="drop-3-top-voices"
        className="drop2-section"
        aria-labelledby="drop3-top-voices-heading"
      >
        <div className="drop2-section-heading">
          <span aria-hidden="true">03</span>
          <div>
            <h2 id="drop3-top-voices-heading">Top voices and inversions</h2>
          </div>
        </div>
        <p className="drop2-lede">
          Rotate the close-position chord first, then drop voice 3. The rotation sets the top voice.
          The bass after the drop sets the inversion. With B on top, Drop 3 gives 1st inversion and
          Drop 2 gives 2nd inversion.
        </p>

        <div className="drop2-table-wrap">
          <table className="drop2-inversion-table">
            <thead>
              <tr>
                <th scope="col">Top voice</th>
                <th scope="col">Drop 3, low → high</th>
                <th scope="col">Finished bass</th>
                <th scope="col">Finished inversion</th>
              </tr>
            </thead>
            <tbody>
              {TOP_VOICES.map((voicing) => (
                <tr key={voicing.topVoice}>
                  <th scope="row">{voicing.topVoice}</th>
                  <td className="drop2-note-sequence">{voicing.notes}</td>
                  <td>{voicing.bass}</td>
                  <td>{voicing.inversion}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section
        id="drop-3-layouts"
        className="drop2-section"
        aria-labelledby="drop3-layouts-heading"
      >
        <div className="drop2-section-heading">
          <span aria-hidden="true">04</span>
          <div>
            <h2 id="drop3-layouts-heading">String layouts</h2>
          </div>
        </div>
        <p className="drop2-lede">
          Drop 3 skips the string above the bass. Keep the skipped string muted.
        </p>
        <div className="voicing-layout-comparison">
          <article>
            <p className="learn-eyebrow">Lower layout</p>
            <p>Bass on low E, A muted, then D–G–B. Used for comping and solo guitar.</p>
            <div
              className="voicing-string-strip"
              aria-label="Lower Drop 3 layout uses low E, D, G and B strings"
            >
              <strong>E</strong>
              <span className="is-skip">×</span>
              <strong>D</strong>
              <strong>G</strong>
              <strong>B</strong>
              <span>e</span>
            </div>
          </article>
          <article>
            <p className="learn-eyebrow">Upper layout</p>
            <p>Bass on A, D muted, then G–B–E. Higher, and suits playing under a melody.</p>
            <div
              className="voicing-string-strip"
              aria-label="Upper Drop 3 layout uses A, G, B and high E strings"
            >
              <span>E</span>
              <strong>A</strong>
              <span className="is-skip">×</span>
              <strong>G</strong>
              <strong>B</strong>
              <strong>e</strong>
            </div>
          </article>
        </div>
      </section>

      <section id="use-drop-3" className="drop2-section" aria-labelledby="drop3-use-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">05</span>
          <div>
            <h2 id="drop3-use-heading">Uses</h2>
          </div>
        </div>
        <div className="drop2-two-column voicing-use-grid">
          <div>
            <h3>Use Drop 3</h3>
            <ul>
              <li>When the bass note needs its own register.</li>
              <li>For solo guitar and small groups.</li>
              <li>When the upper three voices support a melody.</li>
            </ul>
          </div>
          <div>
            <h3>Limits</h3>
            <p>
              Drop 3 is not better than Drop 2. Its low bass note can clash with a bass player and
              thicken a dense arrangement. Choose the family that suits the band.
            </p>
          </div>
        </div>
      </section>

      <footer className="drop2-next">
        <h2>Next</h2>
        <div className="drop2-next-grid">
          <Link to={`${APP_ROUTES.chordLibrary}?family=drop-3`}>
            <span>Reference</span>
            <strong>Drop 3 shapes</strong>
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
          <Link to={`${APP_ROUTES.workbench}?family=drop-3`}>
            <span>Apply</span>
            <strong>Open Drop 3 in the Workbench</strong>
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
          <Link to={APP_ROUTES.drop2Intro}>
            <span>Compare</span>
            <strong>Review Drop 2</strong>
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
          <Link to={APP_ROUTES.fourNoteVoicings}>
            <span>Foundation</span>
            <strong>All voicing families</strong>
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
        </div>
      </footer>
    </article>
  );
}
